import { jsPDF } from 'jspdf';
import { isAdvancePercentMode, resolveAdvanceAmount } from '@pomoshnik/shared';

import { isCompositionRowEmpty } from '../compositionRowUtils';
import { ADVANCE_ROW_ID, REMAINDER_ROW_ID } from '../conditionRows';
import type { CompositionRow, QuoteState } from '../types';

/**
 * PR-H6 (ADR-0029) — full PDF redesign per template «КП №2.jpg». Removed
 * navy header / teal badges / amber Total box → white background + bordered
 * tables + centered section headers + «тенге» word currency.
 *
 * PR-H7 (ADR-0030) — restructure title block per «КП шаблон.jpg»:
 *   - Title теперь 2 строки: line 1 = «Коммерческое предложение №N» (28pt
 *     bold centered), line 2 = state.furnitureType as-is (20pt centered).
 *   - Одна underline снизу под обеими строками.
 *   - Bordered «Тип мебели:» row из body документа удалён (furniture type
 *     теперь в title line 2 — duplicate row избыточен).
 *   - FURNITURE_TYPE_PDF_LABELS mapping удалён — PDF subtitle = state.furnitureType
 *     as-is (Calculator entry labels = source of truth, см. ADR-0030 §6-7).
 *
 * Cyrillic: jsPDF default helvetica НЕ поддерживает кириллицу — все русские
 * буквы превращаются в ▯. Грузим PT Sans Regular + Bold лениво при первом
 * вызове (parallel Promise.all), кешируем base64 в module-level cache.
 */

const FONT_NAME = 'PTSans';
const FONT_FILES = {
  regular: {
    url: '/fonts/PTSans-Regular.ttf',
    vfs: 'PTSans-Regular.ttf',
    style: 'normal' as const,
  },
  bold: { url: '/fonts/PTSans-Bold.ttf', vfs: 'PTSans-Bold.ttf', style: 'bold' as const },
} as const;

type FontKey = keyof typeof FONT_FILES;

const cachedFontBase64: Record<FontKey, string | null> = { regular: null, bold: null };

async function loadFontBase64(key: FontKey): Promise<string> {
  const cached = cachedFontBase64[key];
  if (cached !== null) return cached;

  const file = FONT_FILES[key];
  const response = await fetch(file.url);
  if (!response.ok) {
    throw new Error(`Не удалось загрузить шрифт (${response.status} ${response.statusText})`);
  }
  const buffer = await response.arrayBuffer();
  if (buffer.byteLength < 1024 || !hasTrueTypeMagic(buffer)) {
    throw new Error(`Шрифт повреждён или не TTF (${buffer.byteLength} bytes)`);
  }
  const base64 = arrayBufferToBase64(buffer);
  cachedFontBase64[key] = base64;
  return base64;
}

function hasTrueTypeMagic(buffer: ArrayBuffer): boolean {
  const head = new Uint8Array(buffer, 0, 4);
  return head[0] === 0x00 && head[1] === 0x01 && head[2] === 0x00 && head[3] === 0x00;
}

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.subarray(i, Math.min(i + chunkSize, bytes.length));
    binary += String.fromCharCode(...chunk);
  }
  return btoa(binary);
}

/**
 * PR-H6 — Layout constants (mm) for white-background bordered-table design.
 * pageW/pageH A4 portrait. marginX/marginTop body margins, marginBottom keeps
 * ensureSpace away from page edge. rowH стандартная высота одной строки
 * bordered-table (12mm для single rows, 10mm для multi-row tables).
 */
const LAYOUT = {
  pageW: 210,
  pageH: 297,
  marginX: 12,
  marginTop: 15,
  marginBottom: 15,
  continuationTop: 15,
  // Vertical advance gaps between blocks. PR-H6 visual adjustment (Aibek
  // 2026-05-28 post-preview verify): gapAfterCompany 12 → 18 (~50% increase)
  // для visual breathing room между 21pt italic «ЛучшаяМебель» и centered
  // title. Visual gap (gap − cap_height) растёт пропорционально с font size
  // increase: было 12 − 5 (14pt) = 7mm, стало 18 − 7.5 (21pt) = 10.5mm (×1.5).
  gapAfterCompany: 18,
  gapAfterTitle: 8,
  /**
   * PR-H7 adjustment (Aibek 2026-05-29 post-CF preview verify): explicit
   * vertical gap между title line 1 («Коммерческое предложение №N», 28pt cap)
   * и title line 2 (furniture type subtitle, also 28pt после adjustment).
   * Cap-height advance (10mm) сам по себе оставляет 0mm visual gap между
   * baseline of line 1 и top of line 2 — без этого constant обе строки
   * слипались. 6mm gap = ~½ × cap height обеспечивает «заметный пробел»
   * per Aibek visual review без excessive whitespace.
   */
  gapBetweenTitleLines: 6,
  gapAfterDate: 8,
  gapAfterRow: 6,
  gapAfterTable: 6,
  gapAfterCenteredH2: 4,
  gapAfterLeftH2: 4,
  // Row heights for tables.
  rowSingleH: 12, // «Тип мебели:» / «Стоимость:»
  rowDataH: 10, // multi-row table data rows
  rowHeaderH: 11, // bordered table header row («Название» / «Тип» / «Бренд»)
  // Strokes.
  borderLineWidth: 0.3,
  underlineWidth: 0.5,
  underlineGap: 2,
  textPaddingX: 3,
} as const;

/**
 * PR-H6 — Font sizes (pt) per «КП №2.jpg» reference. Title 28pt, centered h2
 * 20pt («Комплектация», «Условия»), left h2 14pt («Контакты исполнителя:»,
 * «Комментарии:»), table header 14pt, table data 14pt, date 14pt, total cost
 * 18pt («2 460 тенге»).
 *
 * PR-H6 visual adjustment (Aibek 2026-05-28 post-preview verify): companyName
 * 14pt → 21pt (×1.5) для большей prominence — это «brand banner» позиция
 * (top-left перед title).
 */
const FONT_SIZES = {
  companyName: 21,
  headerTitle: 28,
  /**
   * PR-H7 (ADR-0030 §7) — 2nd line title (furniture type subtitle).
   *
   * PR-H7 adjustment (Aibek 2026-05-29 post-CF preview verify): 20pt → 28pt.
   * Обе строки title должны быть одинакового bold размера per «КП шаблон.jpg»
   * — Aibek vision «обе строки одинакового веса». Изначальная hierarchy
   * (28pt > 20pt) визуально не соответствовала reference. Spacing между
   * строками теперь обеспечивается через `LAYOUT.gapBetweenTitleLines`.
   */
  headerSubtitle: 28,
  headerDate: 14,
  sectionH2Centered: 20,
  sectionH2Left: 14,
  tableHeader: 14,
  tableData: 14,
  totalCost: 18,
} as const;

interface RenderCursor {
  y: number;
}

function formatDateRu(date: Date): string {
  const dd = String(date.getDate()).padStart(2, '0');
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const yyyy = date.getFullYear();
  return `${dd}.${mm}.${yyyy} г.`;
}

/**
 * PR-H6 — PDF-specific currency formatter. UI keeps existing «X ₸» format
 * (per Aibek Q2 — «тенге» только в PDF). thousand separator через ru-RU
 * Intl locale.
 */
function formatCurrencyForPdf(value: number | null): string {
  if (value === null) return '—';
  return `${value.toLocaleString('ru-RU')} тенге`;
}

/**
 * PR-H6 — PDF-specific condition display. Reference показывает Условия
 * values как plain numbers («1500», «960», «12 мес.», «2 этаж») без
 * валютного суффикса. Остаток computed value передаётся через
 * `computedRemainder` arg (same source of truth as UI per
 * conditionRows.ts:computeRemainder).
 *
 * Differs from UI conditions formatting (inline в SectionConditions.tsx
 * через `${value.toLocaleString('ru-RU')} ₸` pattern) which suffixes «₸» для
 * cost-related rows. UI vs PDF context separation per single-responsibility
 * (UI rendering и PDF rendering — разные concerns).
 */
function formatConditionDisplayForPdf(
  value: string,
  unit: string | undefined,
  isRemainder: boolean,
  computedRemainder: number,
): string {
  if (isRemainder) return computedRemainder.toLocaleString('ru-RU');
  if (!value) return '—';
  return unit ? `${value} ${unit}` : value;
}

function ensureSpace(pdf: jsPDF, cursor: RenderCursor, needed: number): void {
  if (cursor.y + needed > LAYOUT.pageH - LAYOUT.marginBottom) {
    pdf.addPage();
    cursor.y = LAYOUT.continuationTop;
  }
}

/**
 * Top-left company name (PR-H4 preserved through PR-H6 redesign). 14pt italic
 * black, x=marginX, y=cursor (baseline:top). Empty/whitespace → no-op (cursor
 * remains for centered title to start fresh).
 */
function drawCompanyTopLeft(
  pdf: jsPDF,
  companyName: string | undefined,
  cursor: RenderCursor,
): void {
  const trimmed = companyName?.trim();
  if (!trimmed) return;
  pdf.setFont(FONT_NAME, 'italic');
  pdf.setFontSize(FONT_SIZES.companyName);
  pdf.text(trimmed, LAYOUT.marginX, cursor.y, { baseline: 'top' });
  cursor.y += LAYOUT.gapAfterCompany;
}

/**
 * PR-H7 (ADR-0030 §7) — Centered title block из 2 строк + одна underline под
 * обеими per «КП шаблон.jpg»:
 *   - Line 1: «Коммерческое предложение №N» — 28pt bold centered (~10mm cap)
 *   - Line 2: furniture type as-is — 20pt centered (~7.5mm cap), only if non-empty
 *   - Underline (margin-to-margin) ПОД обеими строками — single line
 *
 * Если `line2` пустая (state.furnitureType=null/empty) — рендерим только line 1
 * + underline (graceful fallback).
 */
function drawCenteredTitleTwoLines(
  pdf: jsPDF,
  line1: string,
  line2: string,
  cursor: RenderCursor,
): void {
  pdf.setFont(FONT_NAME, 'bold');

  // Line 1 — render + advance only if non-empty (defensive: BottomActionButtons
  // всегда генерит непустую line 1 «Коммерческое предложение №N», но
  // `extractTitleLine1` теоретически может вернуть '' если quoteName начинается
  // с ` — ` separator).
  const trimmedLine1 = line1.trim();
  if (trimmedLine1) {
    pdf.setFontSize(FONT_SIZES.headerTitle);
    pdf.text(trimmedLine1, LAYOUT.pageW / 2, cursor.y, { align: 'center', baseline: 'top' });
    // PR-H7 adjustment: cap 28pt ≈ 10mm + explicit gap до line 2 (см.
    // LAYOUT.gapBetweenTitleLines docstring). Только если line 2 будет
    // рендериться — иначе underline ставится сразу после line 1.
    cursor.y += line2.trim() ? 10 + LAYOUT.gapBetweenTitleLines : 10;
  }

  const trimmedLine2 = line2.trim();
  if (trimmedLine2) {
    pdf.setFontSize(FONT_SIZES.headerSubtitle);
    pdf.text(trimmedLine2, LAYOUT.pageW / 2, cursor.y, { align: 'center', baseline: 'top' });
    // PR-H7 adjustment: cap 28pt ≈ 10mm (раньше 7.5mm для 20pt).
    cursor.y += 10;
  }

  const underlineY = cursor.y + LAYOUT.underlineGap;
  pdf.setDrawColor(0, 0, 0);
  pdf.setLineWidth(LAYOUT.underlineWidth);
  pdf.line(LAYOUT.marginX, underlineY, LAYOUT.pageW - LAYOUT.marginX, underlineY);
  cursor.y = underlineY + LAYOUT.gapAfterTitle;
}

/**
 * Left-aligned date «DD.MM.YYYY г.», 14pt normal black, x=marginX,
 * baseline:top. Advances cursor by cap height (~5mm) + gap.
 */
function drawLeftDate(pdf: jsPDF, dateStr: string, cursor: RenderCursor): void {
  pdf.setFont(FONT_NAME, 'normal');
  pdf.setFontSize(FONT_SIZES.headerDate);
  pdf.text(dateStr, LAYOUT.marginX, cursor.y, { baseline: 'top' });
  cursor.y += 5 + LAYOUT.gapAfterDate;
}

/**
 * Centered section h2 («Комплектация», «Условия»). 20pt bold black, центр
 * at pageW/2, baseline:top. Advances cursor by cap ~7mm + gap.
 */
function drawCenteredSectionH2(pdf: jsPDF, text: string, cursor: RenderCursor): void {
  ensureSpace(pdf, cursor, 7 + LAYOUT.gapAfterCenteredH2 + LAYOUT.rowDataH);
  pdf.setFont(FONT_NAME, 'bold');
  pdf.setFontSize(FONT_SIZES.sectionH2Centered);
  pdf.text(text, LAYOUT.pageW / 2, cursor.y, { align: 'center', baseline: 'top' });
  cursor.y += 7 + LAYOUT.gapAfterCenteredH2;
}

/**
 * Left-aligned section h2 («Контакты исполнителя:», «Комментарии:»). 14pt
 * bold black, x=marginX, baseline:top.
 */
function drawLeftSectionH2(pdf: jsPDF, text: string, cursor: RenderCursor): void {
  ensureSpace(pdf, cursor, 5 + LAYOUT.gapAfterLeftH2 + LAYOUT.rowDataH);
  pdf.setFont(FONT_NAME, 'bold');
  pdf.setFontSize(FONT_SIZES.sectionH2Left);
  pdf.text(text, LAYOUT.marginX, cursor.y, { baseline: 'top' });
  cursor.y += 5 + LAYOUT.gapAfterLeftH2;
}

/**
 * Single-row bordered 2-col table («Тип мебели: / Кухонный гарнитур»,
 * «Стоимость: / 2 460 тенге»). Label left-aligned, value centered (default)
 * либо right per options.
 *
 * Default label width 50% / value 50%. Black 0.3mm borders. Padding 3mm
 * horizontal. Single row height 12mm (LAYOUT.rowSingleH).
 */
function drawBorderedRow(
  pdf: jsPDF,
  label: string,
  value: string,
  cursor: RenderCursor,
  options?: { labelWidthPct?: number; valueBold?: boolean },
): void {
  const rowH = LAYOUT.rowSingleH;
  ensureSpace(pdf, cursor, rowH + LAYOUT.gapAfterRow);
  const tableW = LAYOUT.pageW - 2 * LAYOUT.marginX;
  const labelW = tableW * (options?.labelWidthPct ?? 0.5);

  pdf.setDrawColor(0, 0, 0);
  pdf.setLineWidth(LAYOUT.borderLineWidth);
  pdf.rect(LAYOUT.marginX, cursor.y, tableW, rowH);
  pdf.line(LAYOUT.marginX + labelW, cursor.y, LAYOUT.marginX + labelW, cursor.y + rowH);

  pdf.setTextColor(0, 0, 0);
  pdf.setFont(FONT_NAME, 'bold');
  pdf.setFontSize(FONT_SIZES.tableData);
  pdf.text(label, LAYOUT.marginX + LAYOUT.textPaddingX, cursor.y + rowH / 2, {
    baseline: 'middle',
  });

  pdf.setFont(FONT_NAME, options?.valueBold ? 'bold' : 'normal');
  pdf.setFontSize(options?.valueBold ? FONT_SIZES.totalCost : FONT_SIZES.tableData);
  const valueCenterX = LAYOUT.marginX + labelW + (tableW - labelW) / 2;
  pdf.text(value, valueCenterX, cursor.y + rowH / 2, {
    align: 'center',
    baseline: 'middle',
  });

  cursor.y += rowH + LAYOUT.gapAfterRow;
}

/**
 * Multi-row bordered table (Комплектация 3-col with header, Условия 2-col
 * no header, Контакты 2-col no header).
 *
 * @param headers — array of column headers (e.g., ['Название', 'Тип', 'Бренд'])
 *                  либо `null` для no-header table (Условия / Контакты).
 * @param rows — array of row arrays (e.g., [['Столешница', 'ЛДСП', 'Скиф'], ...]).
 *               Empty cells render as '—' fallback.
 * @param colWidthsPct — array of column width percentages (must sum to 1).
 *                       Example: [0.5, 0.25, 0.25] для 3-col Комплектация.
 *
 * Header row (if provided): bold 14pt, padding 3mm, height 11mm.
 * Data rows: normal 14pt, padding 3mm, height 10mm.
 * First column (label) left-aligned, остальные columns centered.
 * Pagination: header repeats on new page (if headers provided).
 */
function drawBorderedTable(
  pdf: jsPDF,
  headers: string[] | null,
  rows: string[][],
  cursor: RenderCursor,
  colWidthsPct: number[],
): void {
  const tableW = LAYOUT.pageW - 2 * LAYOUT.marginX;
  const colWidths = colWidthsPct.map((pct) => tableW * pct);

  pdf.setDrawColor(0, 0, 0);
  pdf.setLineWidth(LAYOUT.borderLineWidth);

  const drawTableHeader = (): void => {
    if (!headers) return;
    pdf.rect(LAYOUT.marginX, cursor.y, tableW, LAYOUT.rowHeaderH);
    let xCursor = LAYOUT.marginX;
    for (let i = 0; i < headers.length - 1; i++) {
      xCursor += colWidths[i] ?? 0;
      pdf.line(xCursor, cursor.y, xCursor, cursor.y + LAYOUT.rowHeaderH);
    }
    pdf.setFont(FONT_NAME, 'bold');
    pdf.setFontSize(FONT_SIZES.tableHeader);
    pdf.setTextColor(0, 0, 0);
    xCursor = LAYOUT.marginX;
    for (let i = 0; i < headers.length; i++) {
      pdf.text(headers[i] ?? '', xCursor + LAYOUT.textPaddingX, cursor.y + LAYOUT.rowHeaderH / 2, {
        baseline: 'middle',
      });
      xCursor += colWidths[i] ?? 0;
    }
    cursor.y += LAYOUT.rowHeaderH;
  };

  ensureSpace(pdf, cursor, (headers ? LAYOUT.rowHeaderH : 0) + LAYOUT.rowDataH);
  drawTableHeader();

  pdf.setFont(FONT_NAME, 'normal');
  pdf.setFontSize(FONT_SIZES.tableData);
  pdf.setTextColor(0, 0, 0);

  for (const row of rows) {
    if (cursor.y + LAYOUT.rowDataH > LAYOUT.pageH - LAYOUT.marginBottom) {
      pdf.addPage();
      cursor.y = LAYOUT.continuationTop;
      drawTableHeader();
      pdf.setFont(FONT_NAME, 'normal');
      pdf.setFontSize(FONT_SIZES.tableData);
    }

    pdf.rect(LAYOUT.marginX, cursor.y, tableW, LAYOUT.rowDataH);
    let xCursor = LAYOUT.marginX;
    for (let i = 0; i < row.length - 1; i++) {
      xCursor += colWidths[i] ?? 0;
      pdf.line(xCursor, cursor.y, xCursor, cursor.y + LAYOUT.rowDataH);
    }

    xCursor = LAYOUT.marginX;
    for (let i = 0; i < row.length; i++) {
      const cellValue = row[i] || '—';
      const colW = colWidths[i] ?? 0;
      if (i === 0) {
        pdf.text(cellValue, xCursor + LAYOUT.textPaddingX, cursor.y + LAYOUT.rowDataH / 2, {
          baseline: 'middle',
        });
      } else {
        const cellCenterX = xCursor + colW / 2;
        pdf.text(cellValue, cellCenterX, cursor.y + LAYOUT.rowDataH / 2, {
          align: 'center',
          baseline: 'middle',
        });
      }
      xCursor += colW;
    }
    cursor.y += LAYOUT.rowDataH;
  }

  cursor.y += LAYOUT.gapAfterTable;
}

/**
 * Wrap-aware multi-line text для «Комментарии» section. 14pt normal black,
 * margin-relative, no border (per reference template — comments в plain text
 * под left-aligned header).
 */
function drawWrappedText(pdf: jsPDF, cursor: RenderCursor, text: string): void {
  pdf.setTextColor(0, 0, 0);
  pdf.setFont(FONT_NAME, 'normal');
  pdf.setFontSize(FONT_SIZES.tableData);
  const lines = pdf.splitTextToSize(text, LAYOUT.pageW - 2 * LAYOUT.marginX) as string[];
  for (const line of lines) {
    ensureSpace(pdf, cursor, 6);
    pdf.text(line, LAYOUT.marginX, cursor.y, { baseline: 'top' });
    cursor.y += 6;
  }
  cursor.y += LAYOUT.gapAfterRow;
}

/**
 * PR-H7 (ADR-0030 §7) — extract title line 1 («Коммерческое предложение №N»)
 * из composite quoteName format «Коммерческое предложение №N — {Furniture}».
 *
 * Граничные случаи:
 *  - quoteName === undefined → default «Коммерческое предложение» (без счётчика)
 *  - quoteName без « — » separator → returns quoteName as-is (preserve full)
 *  - quoteName с separator → returns part before separator (trimmed)
 */
function extractTitleLine1(quoteName: string | undefined): string {
  if (!quoteName) return 'Коммерческое предложение';
  const sepIdx = quoteName.indexOf(' — ');
  if (sepIdx < 0) return quoteName;
  return quoteName.slice(0, sepIdx).trim();
}

/**
 * Render QuoteState → PDF Blob per PR-H6 / ADR-0029 + PR-H7 / ADR-0030 redesign.
 *
 * Structure (per «КП шаблон.jpg» template):
 *  1. Top-left company name (italic, optional, PR-H4 preserved)
 *  2. Centered title — 2 lines (PR-H7):
 *      line 1: «Коммерческое предложение №N» (28pt bold)
 *      line 2: state.furnitureType as-is (20pt) — only if non-empty
 *     Single underline под обеими строками (margin-to-margin)
 *  3. Left-aligned date «DD.MM.YYYY г.»
 *  4. «Комплектация» centered h2 + 3-col bordered table (Название/Тип/Бренд)
 *      (PR-H7: bordered «Тип мебели:» row удалён — furniture type теперь в title line 2)
 *  5. «Стоимость:» bordered row (value «N тенге», bold 18pt)
 *  6. «Условия» centered h2 + 2-col bordered table (label/plain value, no header)
 *  7. «Контакты исполнителя:» left h2 + 2-col bordered table
 *  8. «Комментарии:» left h2 + wrapped text (optional)
 *
 * Pure function — no side effects outside jsPDF instance.
 */
export async function renderQuoteToPdf(
  state: QuoteState,
  quoteName?: string,
  now: Date = new Date(),
): Promise<Blob> {
  const [regularBase64, boldBase64] = await Promise.all([
    loadFontBase64('regular'),
    loadFontBase64('bold'),
  ]);

  const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
  pdf.addFileToVFS(FONT_FILES.regular.vfs, regularBase64);
  pdf.addFont(FONT_FILES.regular.vfs, FONT_NAME, 'normal');
  pdf.addFileToVFS(FONT_FILES.bold.vfs, boldBase64);
  pdf.addFont(FONT_FILES.bold.vfs, FONT_NAME, 'bold');
  // Italic alias maps to normal weight (PT Sans bundle ships regular+bold only;
  // jsPDF will use closest available style for setFont('italic')).
  pdf.addFont(FONT_FILES.regular.vfs, FONT_NAME, 'italic');
  pdf.setFont(FONT_NAME, 'normal');
  pdf.setTextColor(0, 0, 0);

  const cursor: RenderCursor = { y: LAYOUT.marginTop };

  // 1. Top-left company name (optional). PR-H4 preserved.
  drawCompanyTopLeft(pdf, state.companyName, cursor);

  // 2. PR-H7 (ADR-0030 §7) — 2-line title. Line 1 parsed из quoteName (counter
  //    part), line 2 = state.furnitureType as-is (no mapping, per ADR-0030).
  //    Single underline под обеими.
  const titleLine1 = extractTitleLine1(quoteName);
  const titleLine2 = state.furnitureType ?? '';
  drawCenteredTitleTwoLines(pdf, titleLine1, titleLine2, cursor);

  // 3. Left date.
  drawLeftDate(pdf, formatDateRu(now), cursor);

  // 4. «Комплектация» centered h2 + 3-col bordered table.
  //    PR-H7 (ADR-0030 §7): bordered «Тип мебели:» row удалён —
  //    furniture type теперь в title line 2 (duplicate row избыточен).
  //    PDF-only section rename per Aibek Q3 — UI SectionComposition header
  //    остаётся «Состав», PDF uses literal «Комплектация».
  drawCenteredSectionH2(pdf, 'Комплектация', cursor);
  const visibleRows = state.compositionRows.filter(
    (r: CompositionRow) => !isCompositionRowEmpty(r),
  );
  if (visibleRows.length === 0) {
    pdf.setFont(FONT_NAME, 'normal');
    pdf.setFontSize(FONT_SIZES.tableData);
    pdf.text('Нет позиций', LAYOUT.marginX, cursor.y, { baseline: 'top' });
    cursor.y += 6 + LAYOUT.gapAfterTable;
  } else {
    drawBorderedTable(
      pdf,
      ['Название', 'Тип', 'Бренд'],
      visibleRows.map((r) => [r.name || '', r.itemType || '', r.brand || '']),
      cursor,
      [0.5, 0.25, 0.25],
    );
  }

  // 5. «Стоимость:» bordered row, value bold 18pt.
  drawBorderedRow(pdf, 'Стоимость:', formatCurrencyForPdf(state.costWithMargin), cursor, {
    valueBold: true,
  });

  // 6. «Условия» centered h2 + 2-col bordered table.
  //    🔴 CRITICAL preserve: Остаток — COMPUTED value (costWithMargin − аванс),
  //    не raw input. Reference показывает «Остаток: 960».
  //    PR-D2 (ADR-0040): аванс и остаток считаются от ЭФФЕКТИВНОГО аванса
  //    (из ставки «Аванс, %» когда задана, иначе из raw value строки) через
  //    `resolveAdvanceAmount` — единый источник истины с UI/selectors.
  if (state.conditions.length > 0) {
    const advanceRow = state.conditions.find((r) => r.id === ADVANCE_ROW_ID);
    const manualAdvance = Number(advanceRow?.value ?? '');
    const effectiveAdvance = resolveAdvanceAmount(
      state.costWithMargin ?? 0,
      state.advancePercent ?? null,
      Number.isFinite(manualAdvance) ? manualAdvance : 0,
    );
    const computedRemainder = Math.max(0, (state.costWithMargin ?? 0) - effectiveAdvance);
    drawCenteredSectionH2(pdf, 'Условия', cursor);
    drawBorderedTable(
      pdf,
      null,
      state.conditions.map((c) => {
        if (c.id === ADVANCE_ROW_ID && isAdvancePercentMode(state.advancePercent ?? null)) {
          // %-режим: показываем производную сумму (raw value строки пустой/устарел).
          // Стиль как у ручного аванса: «<сумма> ₸» (без thousand-separator).
          return [`${c.label}:`, c.unit ? `${effectiveAdvance} ${c.unit}` : `${effectiveAdvance}`];
        }
        // Ручной режим аванса — прежний вывод байт-в-байт; Остаток — computed.
        return [
          `${c.label}:`,
          formatConditionDisplayForPdf(
            c.value,
            c.unit,
            c.id === REMAINDER_ROW_ID,
            computedRemainder,
          ),
        ];
      }),
      cursor,
      [0.5, 0.5],
    );
  }

  // 7. «Контакты исполнителя:» left h2 + 2-col bordered table.
  if (state.executorContacts.length > 0) {
    drawLeftSectionH2(pdf, 'Контакты исполнителя:', cursor);
    drawBorderedTable(
      pdf,
      null,
      state.executorContacts.map((c) => [`${c.label}:`, c.value || '—']),
      cursor,
      [0.5, 0.5],
    );
  }

  // 8. «Комментарии:» left h2 + wrapped text (optional per Aibek Q2 = B —
  //    keep render if non-empty even though reference template без comments).
  if (state.comments.trim() !== '') {
    drawLeftSectionH2(pdf, 'Комментарии:', cursor);
    drawWrappedText(pdf, cursor, state.comments);
  }

  return pdf.output('blob');
}

/** Reset cache — exposed для unit-test isolation. */
export function __resetFontCacheForTests(): void {
  cachedFontBase64.regular = null;
  cachedFontBase64.bold = null;
}
